import React from 'react';
import { Link } from 'react-router-dom';
import { ValuesCarousel } from '../../components/public/ValuesCarousel';
import { useContentOverrides } from '../../lib/useContentOverrides';

const VALUES_DEFAULT = [
  {
    title: 'Integrity',
    desc: 'Alignment over incentive. Every decision is made in service of the client\u2019s experience \u2014 nothing else.',
  },
  { title: 'Craft', desc: 'Nothing is random. Every detail is designed to shape how the journey feels.' },
  { title: 'Discretion', desc: 'Luxury is privacy. We protect your time, energy, and experience without intrusion.' },
  { title: 'Curiosity', desc: 'We seek depth, not popularity \u2014 designing journeys that feel personal, not programmed.' },
  { title: 'Attunement', desc: 'We listen beyond words \u2014 understanding what you need before it\u2019s fully expressed.' },
  { title: 'Presence', desc: 'We create space within movement \u2014 so you can actually experience where you are.' },
  { title: 'Restraint', desc: 'Less, but better. We edit intentionally so nothing feels excessive or overwhelming.' },
  { title: 'Sovereignty', desc: 'This is not escape. It is return. Every journey reinforces clarity, autonomy, and self-trust.' },
];

export default function AboutPage() {
  const c = useContentOverrides('about');
  const values = c.get('values', VALUES_DEFAULT);
  return (
    <div data-testid="page-about">
      {c.previewMode && (
        <div className="bg-[#1A1A1A] text-white text-center py-2 px-4 text-[11px] tracking-[0.22em] uppercase font-mono" data-testid="about-preview-banner">
          Studio draft preview &mdash; not yet published
        </div>
      )}
      {/* Hero — VÉRAULT as the defining mark */}
      <section className="pt-20 pb-16 sm:pt-32 sm:pb-24 px-6 sm:px-8 text-center" data-testid="about-hero">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-8">{c.get('hero.kicker', 'Our Philosophy')}</p>

        {/* Headline composition: pronunciation kicker above, hairline divider beneath.
            The wordmark is the visual anchor — generous letter-spacing, no extra ornament. */}
        <h1
          className="font-serif text-[56px] sm:text-[88px] lg:text-[112px] text-[#1A1A1A] leading-[0.95] tracking-[0.04em]"
          data-testid="about-hero-wordmark"
        >
          {c.get('hero.wordmark', 'V\u00C9RAULT')}
        </h1>

        <div className="flex items-center justify-center gap-3 mt-7 mb-5 text-[11px] uppercase tracking-[0.32em] text-[#999]">
          <span className="w-8 h-px bg-[#E8E4DC]" aria-hidden="true" />
          <span><span className="font-accent italic normal-case tracking-normal">noun</span> &nbsp;{c.get('hero.pronunciation', '[ veh-ROH ]')}</span>
          <span className="w-8 h-px bg-[#E8E4DC]" aria-hidden="true" />
        </div>

        <p className="font-accent text-base sm:text-lg text-[#6B6B6B] italic leading-relaxed max-w-xl mx-auto">
          {c.get('hero.tagline', 'A sacred return to the self you never lost \u2014 only buried to survive.')}
        </p>
      </section>

      {/* Core Philosophy — grounded prose with grouped editorial cadence */}
      <section className="py-12 sm:py-20 px-6 sm:px-8 bg-white border-t border-[#E8E4DC]" data-testid="about-philosophy">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{c.get('belief.kicker', 'Our Belief')}</p>

          {/* Cadenced headline — brand lands first, then the statement breathes below. */}
          <p className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] leading-[1.2] mb-2 sm:mb-3">
            {c.get('belief.brand_line', 'V\u00C9RAULT Travels')}
          </p>
          <p className="font-serif text-xl sm:text-2xl text-[#6B6B6B] leading-snug italic mb-8 sm:mb-10">
            {c.get('belief.mission_line', 'exists to curate journeys that return you to yourself.')}
          </p>

          <div className="w-10 h-px bg-[#E8E4DC] mx-auto mb-8 sm:mb-10" aria-hidden="true" />

          {/* Pair — quiet contrast */}
          <div className="space-y-1.5 text-base sm:text-[17px] text-[#6B6B6B] leading-[1.75] mb-10 sm:mb-12">
            <p>{c.get('belief.pair_a', 'This is not about seeing more.')}</p>
            <p>{c.get('belief.pair_b', 'It is about feeling more \u2014 with precision.')}</p>
          </div>

          {/* Triplet — tighter internal cadence, larger gap above */}
          <div className="space-y-1.5 text-base sm:text-[17px] text-[#6B6B6B] leading-[1.75]">
            <p>{c.get('belief.triplet_a', 'Every experience is intentional.')}</p>
            <p>{c.get('belief.triplet_b', 'Every detail is considered.')}</p>
            <p>{c.get('belief.triplet_c', 'Every destination reflects who you are becoming.')}</p>
          </div>
        </div>
      </section>

      {/* Mission + Approach — paired editorial columns with varied cadence */}
      <section className="py-12 sm:py-20 px-6 sm:px-8 bg-[#FAFAF8] border-t border-[#E8E4DC]" data-testid="about-mission-approach">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          {/* Mission */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-4">{c.get('mission.kicker', 'Mission')}</p>
            <h2 className="font-serif text-xl sm:text-2xl text-[#1A1A1A] mb-5 leading-snug">{c.get('mission.title', 'What Drives Us')}</h2>
            <div className="w-8 h-px bg-[#E8E4DC] mb-6" aria-hidden="true" />

            <div className="text-sm sm:text-base text-[#6B6B6B] leading-[1.8]">
              <p>{c.get('mission.opening', 'We design travel as a form of refinement.')}</p>

              <div className="my-6 sm:my-7 space-y-1">
                <p>Not excess.</p>
                <p>Not performance.</p>
                <p>Not noise.</p>
              </div>

              <div className="my-6 sm:my-7 space-y-1">
                <p>We remove what is unnecessary.</p>
                <p>We elevate what remains.</p>
              </div>

              <p>{c.get('mission.closing', 'We create space for presence to exist.')}</p>
            </div>
          </div>

          {/* Approach */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-4">{c.get('approach.kicker', 'Approach')}</p>
            <h2 className="font-serif text-xl sm:text-2xl text-[#1A1A1A] mb-5 leading-snug">{c.get('approach.title', 'How We Differ')}</h2>
            <div className="w-8 h-px bg-[#E8E4DC] mb-6" aria-hidden="true" />

            <div className="text-sm sm:text-base text-[#6B6B6B] leading-[1.8]">
              <div className="space-y-1">
                <p>We do not plan trips.</p>
                <p>We compose environments.</p>
              </div>

              <p className="my-6 sm:my-7">
                {c.get('approach.prose', 'Each journey is built through emotional intelligence, spatial awareness, and strategic curation \u2014 balancing beauty, rhythm, and restraint.')}
              </p>

              <div className="space-y-1">
                <p>Luxury, here, is not loud.</p>
                <p>It is deeply considered.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values — editorial manifesto carousel. */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 bg-white border-t border-[#E8E4DC]" data-testid="about-values">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-4">{c.get('values.kicker', 'Values')}</p>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mb-3 leading-snug">{c.get('values.title', 'What We Stand For')}</h2>
            <div className="w-10 h-px bg-[#E8E4DC] mx-auto mb-6" aria-hidden="true" />
            <p className="font-accent italic text-base sm:text-lg text-[#6B6B6B] leading-relaxed">
              {c.get('values.subtitle', 'Eight principles, one philosophy.')}
            </p>
          </div>
          <ValuesCarousel values={values} />
        </div>
      </section>

      {/* CTA — quiet invitational close */}
      <section className="py-16 sm:py-24 px-6 sm:px-8 text-center bg-[#F5F3EE] border-t border-[#E8E4DC]" data-testid="about-cta">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-6">{c.get('cta.kicker', 'What Awaits')}</p>
        <p className="font-accent text-base sm:text-lg text-[#1A1A1A] italic mb-10 max-w-lg mx-auto">
          {c.get('cta.message', 'Every experience begins with a conversation.')}
        </p>
        <Link
          to="/plan"
          className="inline-block px-8 py-3.5 bg-[#1A1A1A] text-white text-sm tracking-wide rounded hover:bg-[#333] transition-colors"
          data-testid="about-cta-button"
        >
          {c.get('cta.button_label', 'Begin Your Experience')}
        </Link>
      </section>
    </div>
  );
}
